import { useState, useEffect } from 'react'
import { supabase } from '../../../shared/lib/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import { useOrg } from '../../../contexts/OrgContext'

function diffParagraphs(oldText, newText) {
  const oldParas = (oldText || '').split(/\n\n+/).filter(Boolean)
  const newParas = (newText || '').split(/\n\n+/).filter(Boolean)

  // LCS on paragraphs
  const m = oldParas.length, n = newParas.length
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0))
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = oldParas[i - 1] === newParas[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1])

  const lcs = []
  let i = m, j = n
  while (i > 0 && j > 0) {
    if (oldParas[i - 1] === newParas[j - 1]) { lcs.unshift(i - 1); i--; j-- }
    else if (dp[i - 1][j] > dp[i][j - 1]) i--
    else j--
  }

  const oldResult = oldParas.map((p, idx) => ({
    text: p,
    status: lcs.includes(idx) ? 'unchanged' : 'removed'
  }))

  const lcsSet = new Set(lcs.map(idx => oldParas[idx]))
  const newResult = newParas.map(p => ({
    text: p,
    status: lcsSet.has(p) ? 'unchanged' : 'added'
  }))

  return { oldResult, newResult }
}

export default function TemplateUpdateReviewModal({ templates, onClose, onUpdate }) {
  const { user } = useAuth()
  const { currentOrg } = useOrg() || {}

  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(false)
  const [newVersionContent, setNewVersionContent] = useState(null)
  const [newVersionStructured, setNewVersionStructured] = useState(null)
  const [versionInfo, setVersionInfo] = useState(null)
  const [declinedMessage, setDeclinedMessage] = useState(false)

  const current = templates[currentIndex]
  const isBatch = templates.length > 1

  useEffect(() => {
    setDeclinedMessage(false)

    if (!current?.org_template_copies_record?.global_template_id) return

    async function fetchLatestVersion() {
      setLoading(true)
      try {
        const { data, error } = await supabase
          .from('global_template_versions')
          .select('version_number, change_summary, created_at, content, structured_content')
          .eq('global_template_id', current.org_template_copies_record.global_template_id)
          .order('created_at', { ascending: false })
          .limit(1)
          .single()

        if (error) {
          console.error('Error fetching version:', error)
          return
        }

        setNewVersionContent(data.content)
        setNewVersionStructured(data.structured_content)
        setVersionInfo({
          version_number: data.version_number,
          change_summary: data.change_summary,
          created_at: data.created_at
        })
      } catch (err) {
        console.error('Error fetching latest version:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchLatestVersion()
  }, [currentIndex, current?.org_template_copies_record?.global_template_id])

  function advanceOrClose() {
    if (isBatch && currentIndex < templates.length - 1) {
      setCurrentIndex(prev => prev + 1)
    } else {
      onClose()
    }
  }

  async function handleDecline() {
    setLoading(true)
    try {
      const { error } = await supabase
        .from('org_template_copies')
        .update({
          status: 'declined',
          update_declined_at: new Date().toISOString(),
          update_declined_by: user.id
        })
        .eq('id', current.org_template_copies_record.id)

      if (error) {
        console.error('Error declining update:', error)
        return
      }

      onUpdate()
      setDeclinedMessage(true)

      setTimeout(() => {
        advanceOrClose()
      }, 1500)
    } catch (err) {
      console.error('Error declining update:', err)
    } finally {
      setLoading(false)
    }
  }

  async function handleAccept() {
    setLoading(true)
    try {
      const resolvedContent = (newVersionContent || '').replace(/\[ORG_NAME\]/g, currentOrg?.name || '[ORG_NAME]')
      const resolvedStructured = newVersionStructured
        ? JSON.parse(JSON.stringify(newVersionStructured).replace(/\[ORG_NAME\]/g, currentOrg?.name || '[ORG_NAME]'))
        : newVersionStructured

      // 1. Overwrite policy_templates content
      const { error: templateError } = await supabase
        .from('policy_templates')
        .update({
          content: resolvedContent,
          structured_content: resolvedStructured
        })
        .eq('id', current.id)

      if (templateError) {
        console.error('Error updating template:', templateError)
        return
      }

      // 2. Update org_template_copies status
      const { error: copyError } = await supabase
        .from('org_template_copies')
        .update({
          status: 'current',
          global_version_at_copy: versionInfo.version_number
        })
        .eq('id', current.org_template_copies_record.id)

      if (copyError) {
        console.error('Error updating copy record:', copyError)
        return
      }

      // 3. Insert revision record
      const { error: revisionError } = await supabase
        .from('org_template_revisions')
        .insert({
          template_id: current.id,
          org_id: currentOrg?.id,
          content: resolvedContent,
          structured_content: resolvedStructured,
          change_summary: `Accepted SSA global template update v${versionInfo.version_number}`,
          edit_method: 'global_update',
          edited_by: user.id
        })

      if (revisionError) {
        console.error('Error inserting revision:', revisionError)
      }

      onUpdate()
      advanceOrClose()
    } catch (err) {
      console.error('Error accepting update:', err)
    } finally {
      setLoading(false)
    }
  }

  const diff = diffParagraphs(current?.content, newVersionContent)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Template Update Review
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {isBatch
                ? `Reviewing ${templates.length} updates`
                : current?.title}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Batch navigation */}
        {isBatch && (
          <div className="border-b border-gray-200 dark:border-gray-700 flex items-center justify-center gap-4 px-6 py-2">
            <button
              onClick={() => setCurrentIndex(prev => prev - 1)}
              disabled={currentIndex === 0}
              className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              &larr; Previous
            </button>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Template {currentIndex + 1} of {templates.length}
            </span>
            <button
              onClick={() => setCurrentIndex(prev => prev + 1)}
              disabled={currentIndex === templates.length - 1}
              className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next &rarr;
            </button>
          </div>
        )}

        {/* Body */}
        <div className="flex gap-6 p-6 flex-1 overflow-hidden">

          {/* Left column - Summary */}
          <div className="w-[28%] flex-shrink-0 space-y-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                {current?.title}
              </h3>
              {current?.domain && (
                <p className="text-xs text-gray-500 dark:text-gray-400">{current.domain}</p>
              )}

              <div className="text-sm text-gray-700 dark:text-gray-300">
                <span className="font-medium">Version </span>
                <span>{current?.org_template_copies_record?.global_version_at_copy}</span>
                <span className="mx-1">&rarr;</span>
                <span className="font-medium">{versionInfo?.version_number || '?'}</span>
              </div>

              {versionInfo?.created_at && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Published {new Date(versionInfo.created_at).toLocaleDateString()}
                </p>
              )}

              {versionInfo?.change_summary && (
                <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Version notes</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300">{versionInfo.change_summary}</p>
                </div>
              )}

              {current?.framework_tags?.length > 0 && (
                <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Frameworks</p>
                  <div className="flex flex-wrap gap-1">
                    {current.framework_tags.map(tag => (
                      <span
                        key={tag}
                        className="text-xs px-2 py-0.5 rounded bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Center + Right - Side-by-side diff */}
          <div className="flex-1 flex gap-4 overflow-hidden">

            {/* Current version */}
            <div className="flex-1 overflow-y-auto">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                Your current version
              </p>
              <div className="space-y-2">
                {loading ? (
                  <p className="text-sm text-gray-400">Loading...</p>
                ) : (
                  diff.oldResult.map((para, idx) => (
                    <p
                      key={idx}
                      className={
                        para.status === 'removed'
                          ? 'text-sm bg-red-50 dark:bg-red-900/20 rounded px-2 py-1 line-through opacity-60 text-gray-700 dark:text-gray-300'
                          : 'text-sm text-gray-700 dark:text-gray-300'
                      }
                    >
                      {para.text}
                    </p>
                  ))
                )}
              </div>
            </div>

            {/* New version */}
            <div className="flex-1 overflow-y-auto">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                New version
              </p>
              <div className="space-y-2">
                {loading ? (
                  <p className="text-sm text-gray-400">Loading...</p>
                ) : (
                  diff.newResult.map((para, idx) => (
                    <p
                      key={idx}
                      className={
                        para.status === 'added'
                          ? 'text-sm bg-green-50 dark:bg-green-900/20 rounded px-2 py-1 text-gray-700 dark:text-gray-300'
                          : 'text-sm text-gray-700 dark:text-gray-300'
                      }
                    >
                      {para.text}
                    </p>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <div>
            {isBatch && currentIndex > 0 && (
              <button
                onClick={() => setCurrentIndex(prev => prev - 1)}
                className="btn-secondary text-sm"
              >
                Back
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {declinedMessage && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Update declined. You can review it again from the template card.
              </span>
            )}
            <button
              onClick={handleDecline}
              disabled={loading || declinedMessage}
              className="btn-secondary text-sm disabled:opacity-50"
            >
              Decline this update
            </button>
            <button
              onClick={handleAccept}
              disabled={loading || declinedMessage}
              className="btn-primary text-sm disabled:opacity-50"
            >
              Accept update
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
